import React, { useEffect, useState } from "react";
import { HttpStatusCode } from "axios";
import { useNotification } from "../../context/NotificationContext";
import { loadAdminUserForm, submitAdminUser } from "../../api/adminApi";

const AdminUser = () => {
  const notify = useNotification();
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    email: '',
    name: '',
    organization: '',
    role: ''
  });

  const [organizationList, setOrganizationList] = useState([]);
  const [roleList, setRoleList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    loadAdminUserForm()
      .then((response) => {
        const { organizationList, roleList } = response.data;
        setOrganizationList(organizationList);
        setRoleList(roleList);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error loading form data:', err);
        setError('Failed to load form data');
        setLoading(false);
      });
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Validate the form fields
  const validateForm = () => {
    let valid = true;
    const errors = { username: '', password: '', email: '', organization: '', role: '' };

    const usernameRegex = /^[a-zA-Z0-9]{5,25}$/;
    if (!usernameRegex.test(formData.username)) {
      errors.username = 'Username must be 5-25 characters long and contain only letters and numbers with no spaces.';
      valid = false;
    }

    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z\d]{8,50}$/;
    if (!passwordRegex.test(formData.password)) {
      errors.password =
        'Password must be 8-50 characters, contain at least one uppercase letter, one lowercase letter, and one number.';
      valid = false;
    }

    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Please enter a valid email address.';
      valid = false;
    }

    if (!formData.organization) {
      errors.organization = 'Please select an organization.';
      valid = false;
    }

    if (!formData.role) {
      errors.role = 'Please select a role.';
      valid = false;
    }

    setFormErrors(errors);
    return valid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      setLoading(true);
      try {
        const payload = { formData };
        const res = await submitAdminUser(formData);
        if (res.status === HttpStatusCode.Created) {
          notify("success", res.data);
        }
        setFormData({ username: '', password: '', email: '', name: '', organization: '', role: '' });
      } catch (error) {
        console.log(error);
        notify("error", "Failed to submit the form");
      } finally {
        setLoading(false);
      }
    }
  };

  if (loading) {
    return <div className="text-center text-lg font-semibold">Loading...</div>;
  }

  if (error) {
    notify("error", error);
  }

  return (
    <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <h2 className="text-2xl font-semibold text-center mb-6">Admin User Registration</h2>
      <form onSubmit={handleSubmit}>
        {/* Username */}
        <div className="mb-4">
          <label htmlFor="username" className="block text-sm font-medium text-gray-700">
            Username
          </label>
          <input
            type="text"
            id="username"
            name="username"
            value={formData.username}
            onChange={handleInputChange}
            required
            className="w-full mt-2 p-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {formErrors.username && <p className="text-red-600 text-sm">{formErrors.username}</p>}
        </div>

        {/* Password */}
        <div className="mb-4">
          <label htmlFor="password" className="block text-sm font-medium text-gray-700">
            Password
          </label>
          <input
            type="password"
            id="password"
            name="password"
            value={formData.password}
            onChange={handleInputChange}
            required
            className="w-full mt-2 p-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {formErrors.password && <p className="text-red-600 text-sm">{formErrors.password}</p>}
        </div>

        {/* Email */}
        <div className="mb-4">
          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
            Email
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            required
            className="w-full mt-2 p-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {formErrors.email && <p className="text-red-600 text-sm">{formErrors.email}</p>}
        </div>

        {/* Name */}
        <div className="mb-4">
          <label htmlFor="name" className="block text-sm font-medium text-gray-700">
            Name
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            className="w-full mt-2 p-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Organization Select */}
        <div className="mb-4">
          <label htmlFor="organization" className="block text-sm font-medium text-gray-700">
            Organization
          </label>
          <select
            id="organization"
            name="organization"
            value={formData.organization}
            onChange={handleInputChange}
            required
            className="w-full mt-2 p-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select Organization</option>
            {organizationList.map((org) => (
              <option key={org.id} value={org.id}>
                {org.name}
              </option>
            ))}
          </select>
          {formErrors.organization && <p className="text-red-600 text-sm">{formErrors.organization}</p>}
        </div>

        {/* Role Select */}
        <div className="mb-4">
          <label htmlFor="role" className="block text-sm font-medium text-gray-700">
            Role
          </label>
          <select
            id="role"
            name="role"
            value={formData.role}
            onChange={handleInputChange}
            required
            className="w-full mt-2 p-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select Role</option>
            {roleList.map((role, index) => (
              <option key={index} value={role}>
                {role}
              </option>
            ))}
          </select>
          {formErrors.role && <p className="text-red-600 text-sm">{formErrors.role}</p>}
        </div>

        {/* Submit Button */}
        <div>
          <button
            type="submit"
            className="w-full py-3 bg-blue-500 text-white font-semibold rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Register
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminUser;
